import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form, Typography } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import RichEditor from '../../components/RichEditor';
import AllCategorySelector from '../../components/AllCategorySelector';
import StaffSelector from '../../components/StaffSelector';

const LessonSubjects = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchCatUid, setSearchCatUid] = useState('');
    const [searchCat2Uid, setSearchCat2Uid] = useState('');
    const [searchStaffUid, setSearchStaffUid] = useState('');
    const [formApi, setFormApi] = useState(null);
    const [showCountModal, setShowCountModal] = useState(false);
    const [currentCountRecord, setCurrentCountRecord] = useState(null);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '隐藏', type: 'warning' }
    };

    const typeMap = {
        1: '视频',
        2: '音频',
        3: '图文'
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/lesson/list_lesson_subject/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    cat_uid: searchCatUid,
                    cat2_uid: searchCat2Uid,
                    staff_uid: searchStaffUid
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/lesson/add_lesson_subject/', {
                ...values,
                price: Math.round(values.price * 100),
                price_ori: Math.round(values.price_ori * 100),
                content: JSON.stringify({ desc: values.content })
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/lesson/edit_lesson_subject/', {
                ...values,
                uid: currentRecord.uid,
                price: Math.round(values.price * 100),
                price_ori: Math.round(values.price_ori * 100),
                content: JSON.stringify({ desc: values.content })
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleEditCount = async (values) => {
        if (!currentCountRecord) return;
        try {
            const response = await request.post('/go/sp/lesson/edit_lesson_subject/', {
                uid: currentCountRecord.uid,
                click_cnt: values.click_cnt,
                buy_cnt: values.buy_cnt
            });
            if (response.errno === 0) {
                Toast.success('更新成功');
                setShowCountModal(false);
                setCurrentCountRecord(null);
                fetchList(pagination.currentPage, pagination.pageSize);
            } else {
                Toast.error(response.errstr || '更新失败');
            }
        } catch (error) {
            console.error('更新失败:', error);
            Toast.error('更新失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/lesson/delete_lesson_subject/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的专栏');
            return;
        }
        try {
            const response = await request.post('/go/sp/lesson/batchedit_lesson_subject/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const formatPrice = (price) => {
        if (typeof price !== 'number') return '-';
        return (price / 100).toFixed(2);
    };

    const columns = [
        {
            title: '专栏信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.image && <img src={record.image} alt={text} style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>类型: {typeMap[record.typ]}</div>
                        {record.typ === 1 && <div style={{ color: 'var(--semi-color-text-2)' }}>时长: {record.duration || 0}秒</div>}
                        <div style={{ color: 'var(--semi-color-text-2)' }}>课程: {record.cat?.name || '-'}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>年级: {record.cat2?.name || '-'}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>教师: {record.staff?.name || '-'}</div>
                    </div>
                </div>
            )
        },
        {
            title: '价格信息',
            dataIndex: 'price',
            width: 150,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>现价: ¥{formatPrice(text)}</div>
                    <div>原价: ¥{formatPrice(record.price_ori)}</div>
                </div>
            )
        },
        {
            title: '统计数据',
            dataIndex: 'stats',
            width: 200,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div
                        style={{ cursor: 'pointer', color: 'var(--semi-color-text-0)' }}
                        onClick={() => { setCurrentCountRecord(record); setShowCountModal(true); }}
                    >
                        点击: {record.click_cnt}
                    </div>
                    <div
                        style={{ cursor: 'pointer', color: 'var(--semi-color-text-0)' }}
                        onClick={() => { setCurrentCountRecord(record); setShowCountModal(true); }}
                    >
                        购买: {record.buy_cnt}次
                    </div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该专栏吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            price: currentRecord.price / 100,
            price_ori: currentRecord.price_ori / 100,
            content: typeof currentRecord.content === 'string' ?
                JSON.parse(currentRecord.content).desc : currentRecord.content
        } : {
            cat_uid: '',
            staff_uid: '',
            price: 0,
            price_ori: 0,
            title: '',
            image: '',
            status: 0,
            sort: 0,
            typ: 1,
            content: '',
            cat2_uid: '',
            staff_uid: ''
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="title"
                    label="专栏标题"
                    rules={[{ required: true, message: '请输入专栏标题' }]}
                />
                <Form.Select
                    field="typ"
                    label="课程类型"
                    rules={[{ required: true, message: '请选择课程类型' }]}
                >
                    {Object.entries(typeMap).map(([value, text]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <Form.InputNumber
                    field="price"
                    label="现价（元）"
                    min={0}
                    precision={2}
                    rules={[{ required: true, message: '请输入现价' }]}
                />
                <Form.InputNumber
                    field="price_ori"
                    label="原价（元）"
                    min={0}
                    precision={2}
                    rules={[{ required: true, message: '请输入原价' }]}
                />
                {formApi?.getValue('typ') === 1 && (
                    <Form.InputNumber
                        field="duration"
                        label="视频时长（秒）"
                        min={0}
                        precision={0}
                        initValue={0}
                    />
                )}
                <Form.Slot label="科目">
                    <AllCategorySelector
                        typ={2}
                        value={formApi?.getValue('cat_uid')}
                        onChange={value => formApi?.setValue('cat_uid', value)}
                        placeholder="请选择科目分类"
                    />
                </Form.Slot>
                <Form.Slot label="年级">
                    <AllCategorySelector
                        typ={1}
                        value={formApi?.getValue('cat2_uid')}
                        onChange={value => formApi?.setValue('cat2_uid', value)}
                        placeholder="请选择年级分类"
                    />
                </Form.Slot>
                <Form.Slot label="教师">
                    <StaffSelector
                        single={true}
                        value={formApi?.getValue('staff_uid')}
                        onChange={(value) => {
                            const staffUid = Array.isArray(value) ? value[0] : value;
                            formApi.setValue('staff_uid', staffUid || '');
                        }}
                        placeholder="请选择教师"
                    />
                </Form.Slot>
                <Form.Slot label="封面图片">
                    <Typography.Text type="tertiary" style={{ marginBottom: '8px', display: 'block' }}>建议图片尺寸为900*500</Typography.Text>
                    <ImageSelector
                        value={formApi?.getValue('image')}
                        onChange={value => formApi?.setValue('image', value)}
                        placeholder="请上传封面图片"
                        width="100%"
                        previewSize={200}
                    />
                </Form.Slot>
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    {Object.entries(statusMap).map(([value, { text }]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    initValue={0}
                    min={0}
                />
                <Form.Slot label="专栏介绍">
                    <RichEditor
                        value={formApi?.getValue('content') || ''}
                        onChange={value => formApi?.setValue('content', value)}
                    />
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.lessonSubjectsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索专栏标题"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="状态筛选"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <AllCategorySelector
                        typ={2}
                        value={searchCatUid}
                        onChange={setSearchCatUid}
                        placeholder="选择科目"
                        style={{ width: 150 }}
                        showClear
                    />
                    <AllCategorySelector
                        typ={1}
                        value={searchCat2Uid}
                        onChange={setSearchCat2Uid}
                        placeholder="选择年级"
                        style={{ width: 150 }}
                        showClear
                    />
                    <StaffSelector
                        single={true}
                        value={searchStaffUid}
                        onChange={(value) => {
                            const staffUid = Array.isArray(value) ? value[0] : value;
                            setSearchStaffUid(staffUid || '');
                        }}
                        placeholder="选择教师"
                        style={{ width: 150 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增专栏</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个专栏吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增专栏"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑专栏"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
            >
                {renderForm('edit')}
            </Modal>

            <Modal
                title={currentCountRecord ? `修改点击/购买 - ${currentCountRecord.title}` : '修改点击/购买'}
                visible={showCountModal}
                onCancel={() => { setShowCountModal(false); setCurrentCountRecord(null); }}
                footer={null}
                width={480}
                centered
            >
                <Form
                    key={currentCountRecord ? `cnt-${currentCountRecord.uid}` : 'cnt-new'}
                    initValues={{
                        click_cnt: currentCountRecord?.click_cnt || 0,
                        buy_cnt: currentCountRecord?.buy_cnt || 0
                    }}
                    onSubmit={handleEditCount}
                    style={{ paddingTop: 12 }}
                >
                    <Form.InputNumber
                        field="click_cnt"
                        label="点击量"
                        min={0}
                        precision={0}
                        step={1}
                        rules={[{ required: true, message: '请输入点击量' }]}
                        style={{ width: '100%' }}
                    />
                    <Form.InputNumber
                        field="buy_cnt"
                        label="购买量"
                        min={0}
                        precision={0}
                        step={1}
                        rules={[{ required: true, message: '请输入购买量' }]}
                        style={{ width: '100%' }}
                    />
                    <div style={{ marginTop: 20, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setShowCountModal(false); setCurrentCountRecord(null); }}>取消</Button>
                            <Button type="primary" htmlType="submit">保存</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default LessonSubjects;